import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiUpload, FiCalendar, FiSave } from 'react-icons/fi';
import '../styles/PubliciteEdit.css';
import { API_BASE_URL, getTenantId } from '../config/api';

const PubliciteEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    visual_url: '',
    short_title: '',
    short_description: '',
    emplacement: 'Accueil',
    start_date: '',
    end_date: '',
    budget: '',
    cost_per_view: '',
    amount_to_pay: '',
    payment_status: 'pending'
  });

  const API_URL = `${API_BASE_URL}/publicites`;
  const TENANT_ID = getTenantId();

  useEffect(() => {
    if (id && id !== 'new') {
      fetchCampaign();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchCampaign = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': TENANT_ID,
        },
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement de la campagne');
      }

      const data = await response.json();
      setCampaign(data);
      
      // Convertir les dates au format input
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };

      setFormData({
        name: data.name || '',
        visual_url: data.visual_url || '',
        short_title: data.short_title || '',
        short_description: data.short_description || '',
        emplacement: data.emplacement || 'Accueil',
        start_date: formatDateForInput(data.start_date) || '',
        end_date: formatDateForInput(data.end_date) || '',
        budget: data.budget || '',
        cost_per_view: data.cost_per_view || '',
        amount_to_pay: data.amount_to_pay || '',
        payment_status: data.payment_status || 'pending'
      });
    } catch (err) {
      console.error('Erreur de chargement :', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Simuler l'upload (dans la vraie implémentation, on uploadera le fichier)
      const fileName = `uploaded-${Date.now()}-${file.name}`;
      setFormData(prev => ({
        ...prev,
        visual_url: `/uploads/visuals/${fileName}`
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const method = id === 'new' ? 'POST' : 'PUT';
      const url = id === 'new' ? API_URL : `${API_URL}/${id}`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': TENANT_ID,
        },
        body: JSON.stringify({
          ...formData,
          // Convertir les dates au format ISO
          start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
          end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la sauvegarde');
      }

      navigate('/publicite');
    } catch (err) {
      console.error('Erreur sauvegarde :', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/publicite');
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (error) {
    return <div className="error">Erreur : {error}</div>;
  }

  const isNewCampaign = id === 'new';
  const pageTitle = isNewCampaign ? 'Nouvelle publicité' : 'Modifier la publicité';

  return (
    <div className="publicite-edit-container">
      <div className="edit-header">
        <button className="back-button" onClick={handleBack}>
          <FiArrowLeft />
          {pageTitle}
        </button>
        <button 
          className="save-button" 
          onClick={handleSubmit}
          disabled={saving}
        >
          <FiSave />
          {saving ? 'Sauvegarde...' : 'Enregistrer'}
        </button>
      </div>

      <div className="edit-content">
        <form onSubmit={handleSubmit} className="campaign-form">
          
          {/* Informations générales */}
          <div className="form-section">
            <h3>Informations générales</h3>
            
            <div className="form-group">
              <label htmlFor="name">Nom de la campagne</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ex: Campagne été 2025"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="emplacement">Emplacement</label>
              <select
                id="emplacement"
                name="emplacement"
                value={formData.emplacement}
                onChange={handleInputChange}
                required
              >
                <option value="Accueil">Accueil</option>
                <option value="Recherche">Recherche</option>
                <option value="Pop up">Pop up</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="start_date">Date de début</label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="end_date">Date de fin</label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* Contenu publicitaire */}
          <div className="form-section">
            <h3>Contenu publicitaire</h3>
            
            <div className="form-group">
              <label htmlFor="visual_upload">Scan du visuel</label>
              <div className="file-upload-container">
                <input
                  type="file"
                  id="visual_upload"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="file-input"
                />
                <label htmlFor="visual_upload" className="file-upload-button">
                  <FiUpload />
                  Choisir un fichier
                </label>
                {formData.visual_url && (
                  <div className="uploaded-file">
                    <img 
                      src={formData.visual_url} 
                      alt="Aperçu" 
                      className="visual-preview"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <span className="file-name" style={{display: 'none'}}>
                      {formData.visual_url.split('/').pop()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="short_title">Titre court</label>
              <input
                type="text"
                id="short_title"
                name="short_title"
                value={formData.short_title}
                onChange={handleInputChange}
                placeholder="Ex: 30% de réduction sur les machines"
                maxLength="100"
              />
              <small>Maximum 100 caractères</small>
            </div>

            <div className="form-group">
              <label htmlFor="short_description">Description courte</label>
              <textarea
                id="short_description"
                name="short_description"
                value={formData.short_description}
                onChange={handleInputChange}
                placeholder="Lorem ipsum sit amet dolor..."
                rows="4"
                maxLength="200"
              />
              <small>Maximum 200 caractères</small>
            </div>
          </div>

          {/* Tarification */}
          <div className="form-section">
            <h3>Tarification</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="budget">Budget total</label>
                <input
                  type="number"
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  placeholder="1000"
                  step="0.01"
                  min="0"
                />
                <small>En FCFA</small>
              </div>
              <div className="form-group">
                <label htmlFor="cost_per_view">Coût par vue</label>
                <input
                  type="number"
                  id="cost_per_view"
                  name="cost_per_view"
                  value={formData.cost_per_view}
                  onChange={handleInputChange}
                  placeholder="0.50"
                  step="0.01"
                  min="0"
                />
                <small>En FCFA</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="amount_to_pay">Montant à payer</label>
                <div className="amount-display">
                  <input
                    type="number"
                    id="amount_to_pay"
                    name="amount_to_pay"
                    value={formData.amount_to_pay}
                    onChange={handleInputChange}
                    placeholder="6000"
                    step="0.01"
                    min="0"
                  />
                  <span className="currency">FCFA</span>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="payment_status">Statut paiement</label>
                <select
                  id="payment_status"
                  name="payment_status"
                  value={formData.payment_status}
                  onChange={handleInputChange}
                >
                  <option value="pending">En attente</option>
                  <option value="paid">Payé</option>
                  <option value="failed">Échec</option>
                </select>
              </div>
            </div>
          </div>

          {/* Boutons d'action en bas */}
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={handleBack}>
              Annuler
            </button>
            <button type="submit" className="submit-button" disabled={saving}>
              {saving ? 'Sauvegarde...' : 'Enregistrer'}
            </button>
          </div>
        </form>

        {/* Aperçu mobile */}
        <div className="mobile-preview">
          <div className="mobile-frame">
            <div className="mobile-header">
              <div className="mobile-status-bar">
                <div className="carrier">WAAGO</div>
                <div className="time">9:41</div>
                <div className="battery">100%</div>
              </div>
              <div className="mobile-search">
                <input type="text" placeholder="Rechercher un prestataire..." readOnly />
              </div>
            </div>
            
            <div className="mobile-content">
              <div className="mobile-ad-preview">
                <div className="ad-content">
                  <div className="ad-header">
                    <div className="ad-avatar">🏢</div>
                    <span>{campaign?.business_name || 'Nom du prestataire'}</span>
                  </div>
                  <div className="ad-body">
                    <h4>{formData.short_title || 'Titre de la publicité'}</h4>
                    <p>{formData.short_description || 'Description de la publicité'}</p>
                    {formData.visual_url && (
                      <div className="ad-image">
                        <img 
                          src={formData.visual_url} 
                          alt="Aperçu publicité"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <button className="ad-button">Consulter</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PubliciteEdit;